import React, { useEffect, useState } from "react";
import { useErrorStore } from "../store/error.store";

/**
 * エラーアイコン
 */
const ErrorIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12,2C17.53,2 22,6.47 22,12C22,17.53 17.53,22 12,22C6.47,22 2,17.53 2,12C2,6.47 6.47,2 12,2M15.59,7L12,10.59L8.41,7L7,8.41L10.59,12L7,15.59L8.41,17L12,13.41L15.59,17L17,15.59L13.41,12L17,8.41L15.59,7Z" />
  </svg>
);

/**
 * 閉じるアイコン
 */
const CloseIcon: React.FC<{ size?: number }> = ({ size = 25 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);

/**
 * スナックバーの位置
 */
interface SnackbarOrigin {
  vertical: "top" | "bottom";
  horizontal: "left" | "center" | "right";
}

/**
 * スナックバーのプロパティ
 */
interface ErrorSnackbarProps {
  anchorOrigin?: SnackbarOrigin;
  sx?: React.CSSProperties | any;
}

/**
 * エラースナックバー
 */
const ErrorSnackbar: React.FC<ErrorSnackbarProps> = ({
  anchorOrigin = { vertical: "bottom", horizontal: "left" },
  sx = {},
}) => {
  const { errorMessage, showError, clearError } = useErrorStore();
  const [visible, setVisible] = useState(false);

  // 表示処理
  useEffect(
    () => {
      if (showError) {
        setVisible(true);

        const timer = setTimeout(() => {
          handleClose();
        }, 3000);
        return () => clearTimeout(timer);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showError]
  );

  // 閉じる処理
  const handleClose = () => {
    setVisible(false);
    setTimeout(() => {
      clearError();
    }, 300);
  };

  if (!showError && !visible) {
    return null;
  }

  // 位置
  const getPositionClasses = () => {
    const { vertical, horizontal } = anchorOrigin;
    let classes = "fixed z-[1000] ";

    if (vertical === "top") classes += "top-4 ";
    else classes += "bottom-4 ";

    if (horizontal === "left") classes += "left-4 ";
    else if (horizontal === "right") classes += "right-4 ";
    else classes += "left-1/2 transform -translate-x-1/2 ";

    return classes;
  };

  return (
    <div
      className={`${getPositionClasses()} transition-all duration-300 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
      }`}
      style={{ ...sx }}
    >
      <div className="flex items-center bg-red-50 border border-red-200 rounded-lg p-3 shadow-lg w-full">
        <div className="flex items-center mr-3 text-red-600">
          <ErrorIcon size={20} />
        </div>

        <div className="flex-1 text-sm text-red-800 break-words">
          {errorMessage}
        </div>

        <button
          onClick={handleClose}
          className="flex items-center ml-3 p-1 text-red-600 hover:bg-red-100 rounded transition-colors duration-200"
          aria-label="閉じる"
        >
          <CloseIcon size={18} />
        </button>
      </div>
    </div>
  );
};

export default ErrorSnackbar;
